import React from 'react';
import { motion } from 'framer-motion';
import { WeeklyGoals } from '@/types/career-sim';
import { Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import ConfettiEffect from './effects/ConfettiEffect';

interface WeekResultsProps {
  weeklyGoals: WeeklyGoals;
  goalsCompleted: boolean;
  onContinue: () => void;
}

const WeekResults: React.FC<WeekResultsProps> = ({ weeklyGoals, goalsCompleted, onContinue }) => {
  const calculateProgress = (current: number, target: number) => {
    return Math.min(100, (current / target) * 100);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {goalsCompleted && <ConfettiEffect />}
      
      <motion.div
        className={`${
          goalsCompleted 
            ? 'bg-gradient-to-br from-green-900/90 to-emerald-900/90 border-green-500/50' 
            : 'bg-gradient-to-br from-gray-900/90 to-slate-900/90 border-gray-500/50'
        } border-2 rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl`}
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <motion.div
            className="text-6xl mb-4"
            animate={{ rotate: goalsCompleted ? [0, -10, 10, -10, 0] : 0 }}
            transition={{ duration: 0.5 }}
          >
            {goalsCompleted ? '🎉' : '📊'}
          </motion.div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Week {weeklyGoals.week} {goalsCompleted ? 'Complete!' : 'Summary'}
          </h2>
          <p className={`text-xl ${goalsCompleted ? 'text-green-200' : 'text-gray-300'}`}>
            {goalsCompleted ? 'All goals achieved!' : 'Keep grinding!'}
          </p>
        </div>

        <div className="bg-black/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Your Progress</h3>
          </div>

          <div className="space-y-4">
            {weeklyGoals.goals.fans && (
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Fans</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.fans.current} / {weeklyGoals.goals.fans.target}
                  </span>
                  {weeklyGoals.goals.fans.current >= weeklyGoals.goals.fans.target ? (
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  )}
                </div>
              </div>
            )}

            {weeklyGoals.goals.funds && (
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Funds</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">
                    ${weeklyGoals.goals.funds.current} / ${weeklyGoals.goals.funds.target}
                  </span>
                  {weeklyGoals.goals.funds.current >= weeklyGoals.goals.funds.target ? (
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  )}
                </div>
              </div>
            )}

            {weeklyGoals.goals.releases && (
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Releases</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.releases.current} / {weeklyGoals.goals.releases.target}
                  </span>
                  {weeklyGoals.goals.releases.current >= weeklyGoals.goals.releases.target ? (
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  )}
                </div>
              </div>
            )}

            {weeklyGoals.goals.reputation && (
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Reputation</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.reputation.current} / {weeklyGoals.goals.reputation.target}
                  </span>
                  {weeklyGoals.goals.reputation.current >= weeklyGoals.goals.reputation.target ? (
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {goalsCompleted && weeklyGoals.reward && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6">
            <h4 className="text-lg font-semibold text-yellow-300 mb-2">🎁 Rewards Earned!</h4>
            <div className="flex flex-wrap gap-3 text-white">
              {weeklyGoals.reward.funds && (
                <span className="bg-green-500/20 px-3 py-1 rounded-full">💰 +${weeklyGoals.reward.funds}</span>
              )}
              {weeklyGoals.reward.reputation && (
                <span className="bg-purple-500/20 px-3 py-1 rounded-full">⭐ +{weeklyGoals.reward.reputation} Rep</span>
              )}
              {weeklyGoals.reward.unlocks && weeklyGoals.reward.unlocks.length > 0 && (
                <span className="bg-blue-500/20 px-3 py-1 rounded-full">🎊 New Actions!</span>
              )}
            </div>
          </div>
        )}

        <motion.button
          onClick={onContinue}
          className={`w-full ${
            goalsCompleted 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' 
              : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
          } text-white font-bold py-4 rounded-xl transition-all shadow-lg`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Continue to Week {weeklyGoals.week + 1} →
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default WeekResults;
